/**
 * Batch Request Manager
 *
 * This module implements request batching to overcome HTTP/1.1 connection pooling limits.
 * Instead of sending individual requests sequentially (which exhausts the browser's
 * connection pool of 6-8 connections), multiple requests can be batched together in a
 * single HTTP POST to /api/router.php.
 *
 * Problem: HTTP/1.1 limits concurrent connections per domain to 6-8.
 * When getConfig returns a large response (15KB+), it blocks other requests in the queue.
 * Solution: Batch related requests into a single HTTP call.
 *
 * The backend router.php already supports batch requests:
 * - Single request: { action, method, data, type }
 * - Batch request: [{ action, method, data, type }, { action, method, data, type }, ...]
 */

import axios from 'redaxios';
import { getAxiosWithAuth } from './repository';
import { authReady } from '../App';

// Define RouterAPIData type locally to avoid import issues
interface RouterAPIData<D = unknown> {
  action: string;
  method: string;
  result: {
    data: D;
    message?: string;
    success: boolean;
    title?: string;
  };
  tid: number;
  type: string;
}

// Store the original axios.post before it gets overridden by batching interceptor
let originalAxiosPost = axios.post;

// Export the original axios post
export { originalAxiosPost };

// Export function to set the original axios.post
export function setOriginalAxiosPost(post: typeof axios.post) {
  originalAxiosPost = post;
}

export interface BatchRequest {
  action: string;
  method: string;
  data?: unknown[];
  type?: string;
  tid?: number;
}

export interface BatchRequestItem<T = unknown> extends BatchRequest {
  id: string;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
}

class BatchRequestManager {
  private queue: BatchRequestItem<any>[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
  private batchTimer: NodeJS.Timeout | null = null;
  private readonly BATCH_DELAY_MS = 100; // Collect requests for 100ms before sending
  private readonly MAX_BATCH_SIZE = 20; // Max requests per batch

  /**
   * Queue a request for batching
   * @param request The request to queue
   * @returns Promise that resolves when response is received
   */
  async queueRequest<T = unknown>(request: BatchRequest): Promise<T> {
    // Send login requests immediately without batching
    if (
      request.action === 'ConnectDB' &&
      request.method === 'getConfig' &&
      request.data &&
      Array.isArray(request.data) &&
      request.data[0]?.id === 1
    ) {
      return this.sendImmediate<T>(request);
    }

    return new Promise((resolve, reject) => {
      const id = `${Date.now()}-${Math.random()}`;

      this.queue.push({
        ...request,
        id,
        resolve,
        reject,
      });

      // Send immediately if batch is full, otherwise wait for timer
      if (this.queue.length >= this.MAX_BATCH_SIZE) {
        this.flush();
      } else if (!this.batchTimer) {
        this.batchTimer = setTimeout(() => this.flush(), this.BATCH_DELAY_MS);
      }
    });
  }

  /**
   * Send a request immediately without batching
   * @param request The request to send
   * @returns Promise that resolves with the response
   */
  private async sendImmediate<T = unknown>(request: BatchRequest): Promise<T> {
    console.log(
      '[Batch] Sending immediate request:',
      request.action,
      request.method,
      'Auth ready:',
      authReady.current
    );
    // Use plain axios for login requests (no auth needed)
    const response = await originalAxiosPost<RouterAPIData>('/api/router.php', {
      action: request.action,
      method: request.method,
      data: request.data,
      type: request.type,
      tid: request.tid,
    });
    console.log('[Batch] Immediate request response:', response.data);
    return response.data.result as T;
  }

  /**
   * Manually flush the queue and send pending requests
   */
  private async flush(): Promise<void> {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.queue.length === 0) return;

    // Check if we have a JWT token for authenticated requests
    const token = localStorage.getItem('jwt_token');
    console.log(
      '[Batch] Checking token for flush, token exists:',
      !!token,
      'authReady:',
      authReady.current
    );
    if (
      !token &&
      this.queue.some(
        (item) =>
          !(
            item.action === 'ConnectDB' &&
            item.method === 'getConfig' &&
            item.data &&
            Array.isArray(item.data) &&
            item.data[0]?.id === 1
          )
      )
    ) {
      console.log(
        '[Batch] Delaying flush - no token for authenticated requests'
      );
      // We have non-login requests but no token, delay the flush
      this.batchTimer = setTimeout(() => this.flush(), 50);
      return;
    }

    // Check if auth is ready (for non-login requests)
    if (
      !authReady.current &&
      this.queue.some(
        (item) =>
          !(
            item.action === 'ConnectDB' &&
            item.method === 'getConfig' &&
            item.data &&
            Array.isArray(item.data) &&
            item.data[0]?.id === 1
          )
      )
    ) {
      console.log('[Batch] Delaying flush - auth not ready');
      // Auth not ready for non-login requests, delay the flush
      this.batchTimer = setTimeout(() => this.flush(), 50);
      return;
    }

    console.log(
      '[Batch] Proceeding with flush, sending',
      this.queue.length,
      'requests'
    );

    const itemsToSend = [...this.queue];
    this.queue = [];

    try {
      // If only one request, send it individually
      if (itemsToSend.length === 1) {
        const item = itemsToSend[0];
        // Use authenticated axios to include JWT token
        const response = await getAxiosWithAuth().post<RouterAPIData>(
          '/api/router.php',
          {
            action: item.action,
            method: item.method,
            data: item.data,
            type: item.type,
            tid: item.tid,
          }
        );
        item.resolve(response.data.result);
      } else {
        // Multiple requests: send as batch
        const batchPayload = itemsToSend.map((item, index) => ({
          action: item.action,
          method: item.method,
          data: item.data,
          type: item.type,
          tid: item.tid || index,
        }));

        // Use authenticated axios to include JWT token
        const response = await getAxiosWithAuth().post<
          RouterAPIData['result'][] | RouterAPIData
        >('/api/router.php', batchPayload);

        // Backend returns array of results for batch requests
        const results = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.result)
          ? response.data.result
          : [response.data.result];

        itemsToSend.forEach((item, index) => {
          const result = results[index];
          if (result && result.success === false && result.message) {
            item.reject(new Error(result.message));
          } else {
            item.resolve(result);
          }
        });
      }
    } catch (error) {
      itemsToSend.forEach((item) => {
        item.reject(error);
      });
    }
  }

  /**
   * Get current queue size (for monitoring/debugging)
   */
  getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Clear the queue and reject all pending requests
   */
  clear(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    this.queue.forEach((item) => {
      item.reject(new Error('Queue cleared'));
    });
    this.queue = [];
  }
}

// Export singleton instance
export const batchRequestManager = new BatchRequestManager();
